// @ts-strict-ignore
import { Box } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { analyticsMessages } from "../../messages";
import { EmptyState } from "../EmptyState";
import { seriesColors, surfaceCardStyle } from "./tokens";

export type CustomerRow = {
  customer: string;
  orders: number;
  gmv: number;
};

interface CustomersTableProps {
  title: string;
  description?: string;
  rows: CustomerRow[];
  columns: {
    customer: string;
    orders: string;
    gmv: string;
  };
  valueFormatter?: (v: number) => string;
  onRowClick?: (row: CustomerRow) => void;
  headerActions?: React.ReactNode;
  loading?: boolean;
  na?: boolean;
  emptyMessage?: string;
}

const initial = (s: string) => {
  const t = (s || "").trim();

  if (!t) return "?";

  return t.charAt(0).toUpperCase();
};

const labelFromEmail = (s: string) => {
  if (!s) return "Guest";

  const at = s.indexOf("@");

  if (at <= 0) return s;

  return s;
};

export const CustomersTable: React.FC<CustomersTableProps> = ({
  title,
  description,
  rows,
  columns,
  valueFormatter,
  onRowClick,
  headerActions,
  loading,
  na,
  emptyMessage,
}) => {
  const intl = useIntl();
  const empty = !na && !loading && (!rows || rows.length === 0);
  const maxGmv = Math.max(1, ...(rows || []).map(r => r.gmv));

  return (
    <Box padding={5} style={{ ...surfaceCardStyle }}>
      <Box marginBottom={4} display="flex" justifyContent="space-between" alignItems="flex-start" gap={3}>
        <Box>
          <Box as="div" fontSize={2} color="default2">
            {title}
          </Box>
          {description && (
            <Box as="div" fontSize={1} color="default2" marginTop={1} style={{ opacity: 0.8 }}>
              {description}
            </Box>
          )}
        </Box>
        {headerActions}
      </Box>

      {na ? (
        <Box padding={6} textAlign="center" color="default2">
          {intl.formatMessage(analyticsMessages.commonNA)}
        </Box>
      ) : loading ? (
        <Box padding={6} textAlign="center" color="default2">
          {intl.formatMessage(analyticsMessages.commonLoading)}
        </Box>
      ) : empty ? (
        <EmptyState message={emptyMessage ?? intl.formatMessage(analyticsMessages.commonNoData)} />
      ) : (
        <Box as="div" overflowX="auto">
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              fontSize: 13,
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#6B7280",
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                  }}
                >
                  {columns.customer}
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "8px 12px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#6B7280",
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                    width: 96,
                  }}
                >
                  {columns.orders}
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "8px 12px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#6B7280",
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                    width: 220,
                  }}
                >
                  {columns.gmv}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const color = seriesColors[i % seriesColors.length];
                const pct = (r.gmv / maxGmv) * 100;
                const label = labelFromEmail(r.customer);

                return (
                  <tr
                    key={`${r.customer}-${i}`}
                    onClick={onRowClick ? () => onRowClick(r) : undefined}
                    style={{
                      transition: "background 120ms ease",
                      cursor: onRowClick ? "pointer" : "default",
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLTableRowElement).style.background =
                        "rgba(15, 23, 42, 0.025)";
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLTableRowElement).style.background = "transparent";
                    }}
                  >
                    <td
                      style={{
                        padding: "10px 12px",
                        borderBottom:
                          i === rows.length - 1 ? "none" : "1px solid rgba(15, 23, 42, 0.06)",
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={3}>
                        <Box
                          as="span"
                          display="inline-flex"
                          alignItems="center"
                          justifyContent="center"
                          __width={20}
                          __height={20}
                          borderRadius={2}
                          style={{
                            background: "rgba(15, 23, 42, 0.06)",
                            color: "#374151",
                            fontSize: 11,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {i + 1}
                        </Box>
                        <Box
                          as="span"
                          display="inline-flex"
                          alignItems="center"
                          justifyContent="center"
                          __width={28}
                          __height={28}
                          borderRadius={3}
                          style={{
                            background: color,
                            color: "#fff",
                            fontSize: 12,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {initial(label)}
                        </Box>
                        <Box
                          as="span"
                          style={{
                            color: "#111827",
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {label}
                        </Box>
                      </Box>
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontFeatureSettings: '"tnum"',
                        color: "#374151",
                        borderBottom:
                          i === rows.length - 1 ? "none" : "1px solid rgba(15, 23, 42, 0.06)",
                      }}
                    >
                      {r.orders}
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        borderBottom:
                          i === rows.length - 1 ? "none" : "1px solid rgba(15, 23, 42, 0.06)",
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={3}>
                        <Box
                          __width={80}
                          __height={6}
                          borderRadius={1}
                          style={{
                            background: "rgba(15, 23, 42, 0.06)",
                            overflow: "hidden",
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            __height={6}
                            style={{
                              width: `${pct}%`,
                              background: color,
                              borderRadius: 1,
                            }}
                          />
                        </Box>
                        <Box
                          as="span"
                          style={{
                            fontWeight: 700,
                            color: "#111827",
                            fontFeatureSettings: '"tnum"',
                            minWidth: 80,
                            textAlign: "right",
                          }}
                        >
                          {valueFormatter ? valueFormatter(r.gmv) : r.gmv}
                        </Box>
                      </Box>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}
    </Box>
  );
};
